/**
 * Interactive consultation controller — TypeScript port of
 * `entropy_app/session.py` (`ConsultationSession`), itself the pull-based
 * re-expression of `strategy._run_loop` / `_ask_question`.
 *
 * The `min_questions` engine runs non-interactively (reads every answer from a
 * fully-known subject). A live app cannot — it must emit one question, pause,
 * and resume when the user answers. This controller:
 *
 *   - reuses the structural ("data-blind") picker, the gate-failure closure
 *     (`closeObservedWithGates`), and the DISC-5 stopping check (`isDetermined`);
 *   - walks the four modules in a per-session-shuffled order;
 *   - within a module commits to a picked candidate — asking that candidate's
 *     gate-ancestor closure (in gate-list order) before re-picking — exactly as
 *     `strategy._ask_question` commits to one chosen candidate per pick.
 *
 * Answers are recorded as cleaned category labels (e.g. "Yes", "No", "Some of
 * the time"); a question whose gate never fires is never posed and is treated
 * as NaN (`null`), which the boolean evaluator reads as `false`.
 */
import {
  Answer,
  atomsIn,
  evaluate,
  Expr,
  featuresIn,
  isDetermined,
  Observed,
} from './boolean';
import {
  CONSULTATION_MODULE_ORDER,
  CRITERIA,
  MODULE_TITLES,
  TARGET_LABELS,
  TARGET_ORDER,
} from './criteria';
import {
  closeObservedWithGates,
  Gate,
  GateGraph,
  isBlocking,
  stemMatchesPredicate,
} from './gates';
import {makeDataBlindPicker, PickFn} from './picker';

export type ModuleSpec = {
  target: string;
  label: string;
  title: string;
  expr: Expr;
  critFeatures: string[];
  atomFeatures: Set<string>;
  pick: PickFn;
};

export type AppContext = {
  gates: GateGraph;
  featurePosition: Map<string, number>;
  modules: Map<string, ModuleSpec>;
};

export type ModuleResult = {
  target: string;
  label: string;
  title: string;
  determined: boolean; // did DISC-5 reach a verdict (vs candidates exhausted)?
  criteriaMet: boolean; // the diagnosis verdict
  nQuestions: number; // questions actually posed in this module
  order: string[]; // feature names in the order asked
};

export type CurrentQuestion = {
  feature: string;
  moduleTarget: string;
  moduleLabel: string;
  moduleTitle: string;
  moduleIndex: number; // 1-based
  moduleCount: number;
  questionsAskedInModule: number;
};

export type SessionOptions = {
  // Override the module order (mainly for tests / parity). Defaults to the
  // fixed clinical order in CONSULTATION_MODULE_ORDER.
  moduleOrder?: string[];
};

/** Build the shared per-target module specs. The data-blind picker and the
 * criteria need no patient data, so this is fully DTA-clean. */
export const buildAppContext = (
  gates: GateGraph,
  featurePosition: Map<string, number>,
): AppContext => {
  const modules = new Map<string, ModuleSpec>();
  for (const target of TARGET_ORDER) {
    const expr = CRITERIA[target];
    modules.set(target, {
      target,
      label: TARGET_LABELS[target],
      title: MODULE_TITLES[target] ?? TARGET_LABELS[target],
      expr,
      critFeatures: featuresIn(expr),
      atomFeatures: new Set(atomsIn(expr).map(a => a.feature)),
      pick: makeDataBlindPicker(expr, featurePosition),
    });
  }
  return {gates, featurePosition, modules};
};

// Sentinel returned by the gate walk when a candidate's gate can never fire
// given the answers so far — the interviewer would never pose it.
const GATED_OUT = Symbol('gated_out');

type ModuleState = {
  spec: ModuleSpec;
  observed: Observed;
  asked: Set<string>;
  order: string[];
  pending: string | null; // candidate currently being walked
};

export class ConsultationSession {
  private ctx: AppContext;

  readonly moduleOrder: string[];

  private moduleIdx = 0;

  private state: ModuleState | null;

  private currentFeature: string | null = null;

  readonly results: ModuleResult[] = [];

  constructor(ctx: AppContext, opts: SessionOptions = {}) {
    this.ctx = ctx;
    this.moduleOrder = (opts.moduleOrder ?? CONSULTATION_MODULE_ORDER).slice();
    this.state = this.newModuleState(this.moduleOrder[0]);
  }

  private newModuleState(target: string): ModuleState {
    const spec = this.ctx.modules.get(target);
    if (!spec) {
      throw new Error(`unknown module target: ${target}`);
    }
    return {spec, observed: new Map(), asked: new Set(), order: [], pending: null};
  }

  get isComplete(): boolean {
    return this.state === null;
  }

  /** Answers recorded so far in the *active* module (feature → cleaned label).
   * Used to fill `[TRAUMA EVENT]` / `[SYMPTOMS ENDORSED]` placeholders. */
  activeObserved(): ReadonlyMap<string, Answer> {
    return this.state ? this.state.observed : new Map<string, Answer>();
  }

  /** The next question to pose, or `null` when complete. Idempotent until
   * `submitAnswer` advances the state. */
  currentQuestion(): CurrentQuestion | null {
    const feature = this.advanceToNextQuestion();
    if (feature === null) {
      return null;
    }
    this.currentFeature = feature;
    const st = this.state as ModuleState;
    return {
      feature,
      moduleTarget: st.spec.target,
      moduleLabel: st.spec.label,
      moduleTitle: st.spec.title,
      moduleIndex: this.moduleIdx + 1,
      moduleCount: this.moduleOrder.length,
      questionsAskedInModule: st.order.length,
    };
  }

  /** Record the user's answer (cleaned category label) and advance. */
  submitAnswer(feature: string, answer: string): void {
    if (this.isComplete) {
      throw new Error('consultation already complete');
    }
    if (feature !== this.currentFeature) {
      throw new Error(
        `unexpected answer for ${feature}; current question is ${this.currentFeature}`,
      );
    }
    const st = this.state as ModuleState;
    st.observed.set(feature, answer);
    st.asked.add(feature);
    st.order.push(feature);
    this.currentFeature = null;
  }

  // --- core loop — mirror of strategy._run_loop / _ask_question ------------

  private advanceToNextQuestion(): string | null {
    while (this.state !== null) {
      const st = this.state;
      const {spec} = st;
      const closed = closeObservedWithGates(st.observed, spec.atomFeatures, this.ctx.gates);
      // 1. DISC-5 stopping rule: stop the module the moment the criteria
      //    expression is determined (positive or negative).
      if (isDetermined(spec.expr, closed)) {
        this.finalizeModule(true, closed);
        continue;
      }
      // 2. Candidate pool exhausted → undetermined (criteriaMet=false).
      const cands = spec.critFeatures.filter(f => !st.asked.has(f));
      if (cands.length === 0) {
        this.finalizeModule(false, closed);
        continue;
      }
      // 3. Pick a candidate, committing to it until its gate closure is fully
      //    walked or it is gated out.
      if (st.pending === null || st.asked.has(st.pending)) {
        st.pending = spec.pick(cands, st.observed);
      }
      // 4. Next concrete question to reach the pending candidate.
      const res = this.nextConcrete(st.pending, st.observed, st.asked);
      if (res === GATED_OUT) {
        st.asked.add(st.pending);
        st.pending = null;
        continue;
      }
      return res as string;
    }
    return null;
  }

  // --- gate walk — mirror of entropy_app.session._next_concrete / _gate_next

  private nextConcrete(
    feature: string,
    observed: Observed,
    asked: Set<string>,
  ): string | typeof GATED_OUT {
    const gates = this.ctx.gates;
    // 1. Direct stem-sub parent: pose the parent chain first; a blocking
    //    ("No"/NaN) parent gates the follow-up out.
    const parent = gates.directParent.get(feature);
    if (parent !== undefined) {
      if (!asked.has(parent)) {
        return this.nextConcrete(parent, observed, asked);
      }
      if (isBlocking(observed.get(parent))) {
        return GATED_OUT;
      }
    }
    // 2. JSON gates.
    for (const gate of gates.gatesByFeature.get(feature) ?? []) {
      const res = this.gateNext(gate, observed, asked);
      if (res === GATED_OUT) {
        return GATED_OUT;
      }
      if (res !== null) {
        return res; // a concrete question to ask toward this gate
      }
      // res === null → gate has fired; check the next gate.
    }
    // 3. All prerequisites satisfied → pose the feature itself.
    return feature;
  }

  private gateNext(
    gate: Gate,
    observed: Observed,
    asked: Set<string>,
  ): string | typeof GATED_OUT | null {
    const {operator: op, gatingStems: stems, predicate: pred} = gate;
    if (op === 'any' || op === 'any_speculative') {
      for (const s of stems) {
        if (asked.has(s)) {
          if (stemMatchesPredicate(observed, s, pred)) {
            return null; // fired at first matching stem
          }
          continue; // answered, no match → try next stem
        }
        const res = this.nextConcrete(s, observed, asked);
        if (res === GATED_OUT) {
          continue; // this stem unreachable → try next
        }
        return res; // pose toward this stem
      }
      return GATED_OUT; // every stem answered/unreachable, none matched
    }
    if (op === 'single') {
      for (const s of stems) {
        if (!asked.has(s)) {
          const res = this.nextConcrete(s, observed, asked);
          return res === GATED_OUT ? GATED_OUT : res;
        }
      }
      if (stems.length === 0) {
        return GATED_OUT;
      }
      return stemMatchesPredicate(observed, stems[0], pred) ? null : GATED_OUT;
    }
    // count_threshold / unknown / manual / all: pose the stems, then treat the
    // gate as fired (the realistic interviewer proceeds to the follow-up).
    for (const s of stems) {
      if (!asked.has(s)) {
        const res = this.nextConcrete(s, observed, asked);
        if (res === GATED_OUT) {
          continue;
        }
        return res;
      }
    }
    return null;
  }

  private finalizeModule(determined: boolean, closed: Observed): void {
    const st = this.state as ModuleState;
    const verdict = evaluate(st.spec.expr, closed);
    this.results.push({
      target: st.spec.target,
      label: st.spec.label,
      title: st.spec.title,
      determined,
      criteriaMet: verdict === true,
      nQuestions: st.order.length,
      order: st.order.slice(),
    });
    this.moduleIdx += 1;
    this.state =
      this.moduleIdx < this.moduleOrder.length
        ? this.newModuleState(this.moduleOrder[this.moduleIdx])
        : null;
  }
}
